import { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, List, Collapse, ListItemText, ListItemIcon, ListItemButton, Skeleton, Stack } from '@mui/material';
import Iconify from './Iconify';

const VIOLET      = 'var(--color-primary-main)';
const VIOLET_BG   = 'rgba(var(--color-primary-main-rgb), 0.08)';
const VIOLET_LGHT = 'var(--color-primary-light)';
const T2          = '#475569';
const T3          = '#94A3B8';
const SURFACE     = '#F8FAFC';

const getIcon = (name) => <Iconify icon={name} width={20} height={20} />;

const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(({ theme }) => ({
  ...theme.typography.body2,
  height: 44,
  position: 'relative',
  textTransform: 'capitalize',
  color: T2,
  fontWeight: 500,
  borderRadius: 10,
  paddingLeft: 14,
  paddingRight: 10,
  marginBottom: 2,
  transition: 'all 0.15s ease',
  '&:hover': {
    background: SURFACE,
    color: VIOLET,
    '& .nav-icon': { color: VIOLET },
  },
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22, height: 22, color: 'inherit',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 36, transition: 'color 0.15s',
});

NavItem.propTypes = { item: PropTypes.object, active: PropTypes.func };

function NavItem({ item, active }) {
  let isActiveRoot = false;
  if (item.path) isActiveRoot = active(item.path);
  useLocation();
  const urlPath = `/${window.location.href.split('/')[3]}`;
  if (item.children?.length > 0) {
    const match = item.children.filter((cc) => cc.path === urlPath);
    if (match.length > 0) isActiveRoot = active(match[0].path);
  }

  const { title, path, icon_name: iconName, info, children = [] } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const activeRootStyle = {
    color: VIOLET,
    fontWeight: 700,
    background: VIOLET_BG,
    borderLeft: `3px solid ${VIOLET}`,
    paddingLeft: '11px',
    '& .nav-icon': { color: VIOLET },
    '&:hover': { background: VIOLET_BG },
  };

  const activeSubStyle = { color: VIOLET, fontWeight: 700 };

  if (children.length > 0) {
    return (
      <>
        <ListItemStyle onClick={() => setOpen((p) => !p)} sx={{ ...(isActiveRoot && activeRootStyle) }}>
          <ListItemIconStyle className="nav-icon">{iconName && getIcon(iconName)}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} sx={{ ml: -0.5, fontSize: '0.875rem' }} />
          {info && info}
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 18, height: 18, mr: 1, color: T3 }}
          />
        </ListItemStyle>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 1 }}>
            {children.map((child) => {
              const isActiveSub = active(child.path);
              return (
                <ListItemStyle key={child.title} component={RouterLink} to={child.path}
                  sx={{ ...(isActiveSub && activeSubStyle), pl: 2 }}>
                  <ListItemIconStyle>
                    <Box component="span" sx={{
                      width: 5, height: 5, ml: 2, borderRadius: '50%',
                      bgcolor: isActiveSub ? VIOLET : T3,
                      transform: isActiveSub ? 'scale(1.6)' : 'scale(1)',
                      transition: 'all 0.2s',
                    }} />
                  </ListItemIconStyle>
                  <ListItemText disableTypography primary={child.title} sx={{ fontSize: '0.83rem' }} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle component={RouterLink} to={path} sx={{ ...(isActiveRoot && activeRootStyle) }}>
      <ListItemIconStyle className="nav-icon">{iconName && getIcon(iconName)}</ListItemIconStyle>
      <ListItemText disableTypography primary={title} sx={{ ml: -0.5, fontSize: '0.875rem' }} />
      {info && info}
    </ListItemStyle>
  );
}

NavSection.propTypes = { navConfig: PropTypes.array };

export default function NavSection({ navConfig, loading, ...other }) {
  const { pathname } = useLocation();
  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  return (
    <Box {...other}>
      <List disablePadding sx={{ px: 1, pt: 0.5 }}>
        {loading && [0,1,2,3,4,5,6,7,8,9,10].map((i) => (
          <Stack key={i} direction="row" alignItems="center" sx={{ px: 1.5, mb: 0.75 }}>
            <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: '8px' }} />
            <Skeleton variant="text" height={32} sx={{ ml: 1.5, flex: 1, borderRadius: '6px' }} />
          </Stack>
        ))}
        {!loading && navConfig.map((item) => <NavItem key={item.title} item={item} active={match} />)}
      </List>
    </Box>
  );
}
